/**
 * chart-of-accounts-templates/api — panel ITE „Wzorce planu kont".
 *
 * Klient API + hooki React Query dla katalogu globalnych wzorców planu kont
 * zarządzanego przez administratorów ITE (mirror `global-dictionaries/api.ts`).
 * Model **COPY-ON-ADOPT** — adopcja u tenanta KOPIUJE pozycje do planu kont firmy,
 * edycja wzorca w tym panelu NIGDY nie propaguje się do już-adoptowanych kont
 * (patrz `docs/ADR/0081-plan-kont-wzorce-globalne-copy-on-adopt.md`). Bez cyklu
 * statusów draft/published/withdrawn (w przeciwieństwie do słowników) — `isActive`
 * wystarcza: tylko aktywne wzorce trafiają do pickera adopcji tenanta.
 *
 * Endpointy (`/api/admin/chart-of-accounts-templates`):
 *   GET    /                          — lista (opcjonalnie ?include_deleted=true)
 *   POST   /                          — utwórz wzorzec
 *   GET    /:id                       — szczegół + pozycje (płaska lista, drzewo składane na froncie)
 *   PUT    /:id                       — edycja meta (name/description/isActive)
 *   DELETE /:id                       — soft-delete (kaskada na pozycje)
 *   POST   /:id/restore               — undelete z kosza (kaskada na pozycje)
 *   POST   /:id/items                 — dodaj pozycję
 *   PUT    /:id/items/:itemId         — edytuj pozycję
 *   DELETE /:id/items/:itemId         — soft-delete pozycji (409 gdy ma dzieci)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

// ============================================================================
// Types
// ============================================================================

export type ChartTemplateAccountType = 'bilansowe' | 'wynikowe' | 'pozabilansowe';
export type ChartTemplateNormalSide = 'debit' | 'credit' | null;

export interface AdminChartTemplate {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  itemCount?: number;
  gcrecord: string | null;
}

export interface AdminChartTemplateItem {
  id: string;
  templateId: string;
  parentId: string | null;
  accountNumber: string;
  name: string;
  accountType: ChartTemplateAccountType;
  isSettlement: boolean;
  normalSide: ChartTemplateNormalSide;
  isPostable: boolean;
  position: number;
}

export interface AdminChartTemplateDetail extends AdminChartTemplate {
  items: AdminChartTemplateItem[];
}

export interface ChartTemplateFormValues {
  name: string;
  description: string;
  isActive: boolean;
}

export interface ChartTemplateItemFormValues {
  parentId: string;
  accountNumber: string;
  name: string;
  accountType: ChartTemplateAccountType;
  isSettlement: boolean;
  normalSide: '' | 'debit' | 'credit';
  isPostable: boolean;
}

export function emptyChartTemplateFormValues(): ChartTemplateFormValues {
  return { name: '', description: '', isActive: true };
}

export function emptyChartTemplateItemFormValues(
  parentId: string | null = null,
): ChartTemplateItemFormValues {
  return {
    parentId: parentId ?? '',
    accountNumber: '',
    name: '',
    accountType: 'bilansowe',
    isSettlement: false,
    normalSide: '',
    isPostable: true,
  };
}

interface ListResponse {
  templates: AdminChartTemplate[];
}

interface DetailResponse {
  template: AdminChartTemplateDetail;
}

interface MutateTemplateResponse {
  template: AdminChartTemplate;
}

interface MutateItemResponse {
  item: AdminChartTemplateItem;
}

// ============================================================================
// Query keys
// ============================================================================

export const CHART_TEMPLATES_LIST_KEY = ['admin', 'chart-of-accounts-templates'] as const;
export const chartTemplateDetailKey = (id: string) =>
  ['admin', 'chart-of-accounts-templates', id] as const;

// ============================================================================
// Payload builders
// ============================================================================

export function buildCreateTemplatePayload(
  values: ChartTemplateFormValues,
): Record<string, unknown> {
  const body: Record<string, unknown> = { name: values.name, isActive: values.isActive };
  if (values.description.trim()) body.description = values.description.trim();
  return body;
}

export function buildUpdateTemplatePayload(
  values: ChartTemplateFormValues,
  original: AdminChartTemplate,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (values.name !== original.name) payload.name = values.name;
  const desc = values.description.trim() || null;
  if (desc !== original.description) payload.description = desc;
  if (values.isActive !== original.isActive) payload.isActive = values.isActive;
  return payload;
}

export function buildCreateItemPayload(
  values: ChartTemplateItemFormValues,
): Record<string, unknown> {
  return {
    parentId: values.parentId || null,
    accountNumber: values.accountNumber,
    name: values.name,
    accountType: values.accountType,
    isSettlement: values.isSettlement,
    normalSide: values.normalSide || null,
    isPostable: values.isPostable,
  };
}

export function buildUpdateItemPayload(
  values: ChartTemplateItemFormValues,
  original: AdminChartTemplateItem,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  const parentId = values.parentId || null;
  if (parentId !== original.parentId) payload.parentId = parentId;
  if (values.accountNumber !== original.accountNumber) payload.accountNumber = values.accountNumber;
  if (values.name !== original.name) payload.name = values.name;
  if (values.accountType !== original.accountType) payload.accountType = values.accountType;
  if (values.isSettlement !== original.isSettlement) payload.isSettlement = values.isSettlement;
  const normalSide = values.normalSide || null;
  if (normalSide !== original.normalSide) payload.normalSide = normalSide;
  if (values.isPostable !== original.isPostable) payload.isPostable = values.isPostable;
  return payload;
}

// ============================================================================
// Hooks — templates
// ============================================================================

export function useChartTemplates(includeDeleted: boolean) {
  return useQuery<AdminChartTemplate[]>({
    queryKey: [...CHART_TEMPLATES_LIST_KEY, { includeDeleted }] as const,
    queryFn: async () => {
      const url = includeDeleted
        ? '/admin/chart-of-accounts-templates?include_deleted=true'
        : '/admin/chart-of-accounts-templates';
      const res = await api.get<ListResponse>(url);
      return res.data.templates;
    },
    retry: false,
  });
}

export function useChartTemplateDetail(id: string | null) {
  return useQuery<AdminChartTemplateDetail>({
    queryKey: id ? chartTemplateDetailKey(id) : ['admin', 'chart-of-accounts-templates', 'none'],
    queryFn: async () => {
      const res = await api.get<DetailResponse>(`/admin/chart-of-accounts-templates/${id}`);
      return res.data.template;
    },
    enabled: !!id,
    retry: false,
  });
}

function useInvalidateChartTemplates() {
  const queryClient = useQueryClient();
  return (id?: string) => {
    queryClient.invalidateQueries({ queryKey: CHART_TEMPLATES_LIST_KEY });
    if (id) queryClient.invalidateQueries({ queryKey: chartTemplateDetailKey(id) });
  };
}

export function useCreateChartTemplate() {
  const invalidate = useInvalidateChartTemplates();
  return useMutation({
    mutationFn: async (values: ChartTemplateFormValues) => {
      const res = await api.post<MutateTemplateResponse>(
        '/admin/chart-of-accounts-templates',
        buildCreateTemplatePayload(values),
      );
      return res.data.template;
    },
    onSuccess: () => invalidate(),
  });
}

export function useUpdateChartTemplate() {
  const invalidate = useInvalidateChartTemplates();
  return useMutation({
    mutationFn: async ({
      id,
      values,
      original,
    }: {
      id: string;
      values: ChartTemplateFormValues;
      original: AdminChartTemplate;
    }) => {
      const res = await api.put<MutateTemplateResponse>(
        `/admin/chart-of-accounts-templates/${id}`,
        buildUpdateTemplatePayload(values, original),
      );
      return res.data.template;
    },
    onSuccess: (tpl) => invalidate(tpl.id),
  });
}

export function useDeleteChartTemplate() {
  const invalidate = useInvalidateChartTemplates();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/chart-of-accounts-templates/${id}`);
      return id;
    },
    onSuccess: (id) => invalidate(id),
  });
}

export function useRestoreChartTemplate() {
  const invalidate = useInvalidateChartTemplates();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<MutateTemplateResponse>(
        `/admin/chart-of-accounts-templates/${id}/restore`,
      );
      return res.data.template;
    },
    onSuccess: (tpl) => invalidate(tpl.id),
  });
}

// ============================================================================
// Hooks — items
// ============================================================================

export function useAddChartTemplateItem(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (values: ChartTemplateItemFormValues) => {
      const res = await api.post<MutateItemResponse>(
        `/admin/chart-of-accounts-templates/${templateId}/items`,
        buildCreateItemPayload(values),
      );
      return res.data.item;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chartTemplateDetailKey(templateId) });
    },
  });
}

export function useUpdateChartTemplateItem(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      itemId,
      values,
      original,
    }: {
      itemId: string;
      values: ChartTemplateItemFormValues;
      original: AdminChartTemplateItem;
    }) => {
      const res = await api.put<MutateItemResponse>(
        `/admin/chart-of-accounts-templates/${templateId}/items/${itemId}`,
        buildUpdateItemPayload(values, original),
      );
      return res.data.item;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chartTemplateDetailKey(templateId) });
    },
  });
}

export function useDeleteChartTemplateItem(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (itemId: string) => {
      await api.delete(`/admin/chart-of-accounts-templates/${templateId}/items/${itemId}`);
      return itemId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chartTemplateDetailKey(templateId) });
    },
  });
}
